import { useQuery, useQueries } from 'react-query'
import { apiGetRelatedContent } from '../api/related-content-api'
import { useUserInfo } from './User'
import { locale } from '../Locale'

const RELATED_CONTENT_KEY = 'related-content'

export const CONNECTOR_PART = 'connector_part'

/**
 * The two gates every Related Content surface passes.
 *
 * The feature has to be switched on, and the provider has to serve that kind of
 * entity. They are not the same question: a Wiretronics customer has the feature
 * on and serves connector tooling only, so asking it about a harness, a device
 * or a DTC would always come back empty.
 *
 * Defined once here because the surfaces that apply it are not alike -- the
 * pinout passes a single kind, RelatedContentFor filters a list of targets -- and
 * two copies of "enabled" would eventually disagree about what it means.
 */
export const useServedEntityKinds = () => {
  const { userInfo } = useUserInfo()

  return {
    isEnabled: userInfo?.features?.related_content ?? false,
    served: userInfo?.features?.related_content_entities ?? [],
  }
}

/** Whether this deployment's provider can answer for one entity kind. */
export const useRelatedContentEnabledFor = (entityKind) => {
  const { isEnabled, served } = useServedEntityKinds()

  return isEnabled && served.includes(entityKind)
}

/**
 * The pinout's connector tooling, keyed by pin.
 *
 * A thin name over useRelatedContent rather than a client of its own: one place
 * builds a related-content request, and the pinout shares its cache entry with
 * the harness-view tooltip and Global Search for the same connector.
 */
export const useTools = ({ vin, componentId, enabled = true }) => {
  const { content, ...rest } = useRelatedContent({
    entityKind: CONNECTOR_PART,
    vin,
    enabled,
    connector_id: componentId,
  })

  return { ...rest, tools: content }
}

/**
 * Related content for one entity, keyed by pin.
 *
 * `identifiers` are whatever names that entity kind's code templates use --
 * { harness_id } for a harness, { acronym, dtc } for a DTC -- and are passed
 * through to the query string as given, so adding a kind needs no change here.
 *
 * Entities with no pin dimension come back under the key "all". The locale goes
 * along because Concept-T localizes its content and rejects a language it does
 * not publish; the backend falls back rather than surfacing that.
 */
export const useRelatedContent = ({
  entityKind,
  vin,
  enabled = true,
  ...identifiers
}) => {
  const {
    status,
    data: content,
    error,
    isFetching,
    isLoading,
    isError,
    isSuccess,
  } = useQuery(
    [RELATED_CONTENT_KEY, entityKind, vin, identifiers, locale],
    async () => {
      const result = await apiGetRelatedContent({
        entityKind,
        vin,
        language: locale,
        ...identifiers,
      })
      return result.data
    },
    {
      refetchOnWindowFocus: false,
      enabled:
        enabled &&
        !!entityKind &&
        !!vin &&
        Object.values(identifiers).some(Boolean),
    },
  )

  return {
    status,
    content,
    error,
    isLoading,
    isFetching,
    isError,
    isSuccess,
  }
}

/**
 * Related content for several entity kinds at once, merged.
 *
 * A connector is described by more than one kind: service information and
 * technical bulletins are published against the connector, maintenance tooling
 * against the same id under its own publication type. A technician looking at a
 * connector wants all of it behind one button, so the kinds are fetched in
 * parallel and their steps concatenated in the order given.
 *
 * `targets` must be stable in length across renders -- it drives a hook.
 */
export const useRelatedContentForTargets = ({
  targets,
  vin,
  enabled = true,
}) => {
  const results = useQueries(
    targets.map(({ entityKind, ...identifiers }) => ({
      queryKey: [RELATED_CONTENT_KEY, entityKind, vin, identifiers, locale],
      queryFn: async () => {
        const result = await apiGetRelatedContent({
          entityKind,
          vin,
          language: locale,
          ...identifiers,
        })
        return result.data
      },
      refetchOnWindowFocus: false,
      enabled:
        enabled &&
        !!entityKind &&
        !!vin &&
        Object.values(identifiers).some(Boolean),
    })),
  )

  return {
    contents: results.map((result) => result.data),
    isLoading: results.some((result) => result.isLoading),
    isError: results.some((result) => result.isError),
  }
}
